import {
    Avatar,
    Badge,
    Box,
    HStack,
    IconButton,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Table,
    TableContainer,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tooltip,
    Tr,
    VStack,
    useColorModeValue,
    useDisclosure
} from '@chakra-ui/react';
import React, { useMemo, useState } from 'react';
import { FaCreditCard, FaEllipsisV, FaMoneyBillWave, FaUndoAlt } from 'react-icons/fa';
import FormField from '../../../components/fields/FormField';
import FormModal from '../../../components/FormModal';
import FormStack from '../../../components/form-stack';
import { translate } from '../../../i18n';
import { GameDto, GameParticipantDto, GameType } from '../../../models/game/dto/game.dto';
import { PaymentIntentDto, PaymentIntentStatus } from '../../../models/payment/dto/payment-intent.dto';
import { UserDto } from '../../../models/user/dto/user.dto';
import { useNavigateToUserDetails } from '../../_shared/calendar/hooks/useNavigateToUserDetails';
import { useGetUser } from '../../user-management/hooks/useGetUser';
import { formatDate } from '../GameDetailsPage';
import { useAdminRefundSeat } from '../hooks/useAdminRefundSeat';
import { useChargePayLaterBooking } from '../hooks/useChargePayLaterBooking';
import { useMarkManualPaymentRefunded } from '../hooks/useMarkManualPaymentRefunded';
import { useMarkPayLaterAsPaid } from '../hooks/useMarkPayLaterAsPaid';
import { parsePrice } from '../../../utils/parse-price';
import PaidByDetailsDrawer from './PaidByDetailsDrawer';
import {
    formatPaidByLabel,
    isCoveredSlot,
} from './paid-by.utils';
import {
    liveRefundCentsByPaymentIntentId,
    retainedCents,
} from './payment-summary.utils';

interface ParticipantsTableProps {
    game: GameDto;
}

interface UserWithPayment {
    user: GameParticipantDto['participant'];
    paymentIntent?: PaymentIntentDto;
    isCurrentParticipant: boolean;
    joinedAt?: string;
    position?: number;
    /**
     * When this row is a covered slot (pay-for-others), `paidBy` is the
     * payer's UserDto and `coveredAmountCents` is this player's share of the
     * batch PI (PI amount divided across the covered players for that PI).
     */
    paidBy?: UserDto;
    coveredAmountCents?: number;
}

interface UserRowProps {
    userWithPayment: UserWithPayment;
    navigateToUserDetails: ((userId: string) => void) | null;
    getPaymentStatusBadge: (paymentIntent?: PaymentIntentDto) => JSX.Element;
    formatAmount: (paymentIntent?: PaymentIntentDto) => string;
    handleChargePayLaterBooking: (paymentIntent: PaymentIntentDto) => void;
    chargePayLaterMutation: ReturnType<typeof useChargePayLaterBooking>;
    markPaidMutation: ReturnType<typeof useMarkPayLaterAsPaid>;
    markRefundedMutation: ReturnType<typeof useMarkManualPaymentRefunded>;
    refundSeatMutation: ReturnType<typeof useAdminRefundSeat>;
    gameId: number;
    onOpenPaidBy: (row: UserWithPayment) => void;
}

const UserRow: React.FC<UserRowProps> = ({
    userWithPayment,
    navigateToUserDetails,
    getPaymentStatusBadge,
    formatAmount,
    handleChargePayLaterBooking,
    chargePayLaterMutation,
    markPaidMutation,
    markRefundedMutation,
    refundSeatMutation,
    gameId,
    onOpenPaidBy,
}) => {
    const hoverBg = useColorModeValue('gray.50', 'gray.600');

    // "Refund seat": a card payment whose owner is no longer in the game (left
    // or was kicked) and that nothing has refunded yet. Manual cash/card rows
    // have their own "mark as refunded" control below; POS rows are refunded
    // from the POS. The API re-checks all of this and reports what it skipped.
    const canRefundSeat =
        !userWithPayment.isCurrentParticipant &&
        !!userWithPayment.paymentIntent &&
        userWithPayment.paymentIntent.status === PaymentIntentStatus.succeeded &&
        !userWithPayment.paymentIntent.paymentMethodType?.startsWith('manual_') &&
        userWithPayment.paymentIntent.paymentMethodType !== 'nextore';

    // If this user is not a current participant and we only have a userId, fetch their details
    const shouldFetchUser = !userWithPayment.isCurrentParticipant &&
        userWithPayment.user.firstName === 'Unknown';

    // Only call the hook if we need to fetch user data
    const { data: fetchedUser } = useGetUser(shouldFetchUser ? userWithPayment.user.id : '');

    // Use fetched user data if available, otherwise use the existing user data
    const displayUser = shouldFetchUser && fetchedUser ? fetchedUser : userWithPayment.user;

    const refundTargetName =
        `${displayUser.firstName ?? ''} ${displayUser.lastName ?? ''}`.trim() || displayUser.id;

    const paidByLabel = formatPaidByLabel(
        userWithPayment.paidBy,
        userWithPayment.coveredAmountCents,
        userWithPayment.paymentIntent?.currency,
    );

    return (
        <Tr
            _hover={{ bg: hoverBg }}
            cursor={navigateToUserDetails ? "pointer" : undefined}
            onClick={navigateToUserDetails ? () => navigateToUserDetails(userWithPayment.user.id) : undefined}
        >
            <Td>
                <HStack spacing={3}>
                    <Avatar
                        size="sm"
                        name={`${displayUser.firstName} ${displayUser.lastName}`}
                        src={displayUser.avatar}
                    />
                    <VStack align="start" spacing={0}>
                        <HStack spacing={2}>
                            <Text fontWeight="medium">
                                {displayUser.firstName} {displayUser.lastName}
                            </Text>
                            {displayUser.isGuest && (
                                <Badge colorScheme="orange" size="sm">
                                    {translate('game.guest')}
                                </Badge>
                            )}
                        </HStack>
                        {!displayUser.isGuest && (
                            <Text fontSize="sm" color="gray.500">
                                {displayUser.email}
                            </Text>
                        )}
                    </VStack>
                </HStack>
            </Td>
            <Td>
                <Badge colorScheme={userWithPayment.isCurrentParticipant ? 'green' : 'gray'}>
                    {userWithPayment.isCurrentParticipant
                        ? translate('game.current_participant')
                        : translate('game.former_participant')}
                </Badge>
            </Td>
            <Td>
                <Text fontSize="sm">
                    {userWithPayment.joinedAt
                        ? formatDate(new Date(userWithPayment.joinedAt))
                        : '-'}
                </Text>
            </Td>
            <Td>
                <HStack spacing={2}>
                    {getPaymentStatusBadge(userWithPayment.paymentIntent)}
                    {userWithPayment.paymentIntent?.paidWithWallet && (
                        <Badge colorScheme="teal">{translate('game.payment_wallet')}</Badge>
                    )}
                    {userWithPayment.paymentIntent?.id?.startsWith('pos_') && (
                        <Badge colorScheme="purple">{translate('game.payment_nextore')}</Badge>
                    )}
                </HStack>
            </Td>
            <Td isNumeric>
                <Text fontSize="sm">
                    {formatAmount(userWithPayment.paymentIntent)}
                </Text>
            </Td>
            <Td
                data-testid="paid-by-cell"
                onClick={(e) => {
                    // Cell-level click should open the drawer, not the
                    // navigate-to-user-details on the row. Stop propagation
                    // when the cell is clickable (i.e. a covered slot).
                    if (paidByLabel) {
                        e.stopPropagation();
                        onOpenPaidBy(userWithPayment);
                    }
                }}
                cursor={paidByLabel ? 'pointer' : 'default'}
            >
                {paidByLabel ? (
                    <Text
                        fontSize="sm"
                        color="brand.600"
                        fontWeight="medium"
                        _hover={{ textDecoration: 'underline' }}
                    >
                        {paidByLabel}
                    </Text>
                ) : (
                    <Text fontSize="sm" color="gray.400">
                        —
                    </Text>
                )}
            </Td>
            <Td>
                {userWithPayment.paymentIntent?.isSetupIntentPayment &&
                    userWithPayment.paymentIntent?.status === PaymentIntentStatus.pay_later && (
                        <Menu strategy="fixed">
                            <MenuButton
                                as={IconButton}
                                aria-label={translate('game.actions')}
                                icon={<FaEllipsisV />}
                                variant="ghost"
                                size="sm"
                                onClick={(e) => e.stopPropagation()}
                                isLoading={chargePayLaterMutation.isLoading || markPaidMutation.isLoading}
                            />
                            <MenuList onClick={(e) => e.stopPropagation()}>
                                <MenuItem
                                    icon={<FaCreditCard />}
                                    onClick={() => handleChargePayLaterBooking(userWithPayment.paymentIntent!)}
                                >
                                    {translate('game.charge_pay_later')}
                                </MenuItem>
                                <MenuItem
                                    icon={<FaCreditCard />}
                                    onClick={() => {
                                        if (window.confirm(translate('game.mark_paid_confirm_message'))) {
                                            markPaidMutation.mutate({
                                                paymentIntentId: userWithPayment.paymentIntent!.id,
                                                method: 'manual_card',
                                            });
                                        }
                                    }}
                                >
                                    {translate('game.mark_paid_card')}
                                </MenuItem>
                                <MenuItem
                                    icon={<FaMoneyBillWave />}
                                    onClick={() => {
                                        if (window.confirm(translate('game.mark_paid_confirm_message'))) {
                                            markPaidMutation.mutate({
                                                paymentIntentId: userWithPayment.paymentIntent!.id,
                                                method: 'manual_cash',
                                            });
                                        }
                                    }}
                                >
                                    {translate('game.mark_paid_cash')}
                                </MenuItem>
                            </MenuList>
                        </Menu>
                    )}
                {canRefundSeat && (
                    <Tooltip label={translate('game.refund_seat_tooltip')}>
                        <IconButton
                            aria-label={translate('game.refund_seat')}
                            icon={<FaUndoAlt />}
                            colorScheme="orange"
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                                e.stopPropagation();
                                const paidWithWallet = !!userWithPayment.paymentIntent?.paidWithWallet;
                                if (
                                    window.confirm(
                                        translate(
                                            paidWithWallet
                                                ? 'game.refund_seat_confirm_wallet'
                                                : 'game.refund_seat_confirm',
                                            { name: refundTargetName },
                                        ),
                                    )
                                ) {
                                    refundSeatMutation.mutate({
                                        gameId,
                                        userId: userWithPayment.user.id,
                                        paidWithWallet,
                                        currency: userWithPayment.paymentIntent?.currency,
                                    });
                                }
                            }}
                            // `isLoading` is shared by every row's button: spin
                            // only the row being refunded, and lock the others
                            // while a refund is in flight.
                            isLoading={
                                refundSeatMutation.isLoading &&
                                refundSeatMutation.variables?.userId === userWithPayment.user.id
                            }
                            isDisabled={refundSeatMutation.isLoading}
                        />
                    </Tooltip>
                )}
                {userWithPayment.paymentIntent?.paymentMethodType?.startsWith('manual_') &&
                    userWithPayment.paymentIntent?.status === PaymentIntentStatus.succeeded && (
                        <Tooltip label={translate('game.mark_refunded')}>
                            <IconButton
                                aria-label={translate('game.mark_refunded')}
                                icon={<FaUndoAlt />}
                                colorScheme="orange"
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (window.confirm(translate('game.mark_refunded_confirm'))) {
                                        markRefundedMutation.mutate(userWithPayment.paymentIntent!.id);
                                    }
                                }}
                                isLoading={markRefundedMutation.isLoading}
                            />
                        </Tooltip>
                    )}
            </Td>
        </Tr>
    );
};

const PaymentsTable: React.FC<ParticipantsTableProps> = ({ game }) => {
    const navigateToUserDetails = useNavigateToUserDetails();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const chargePayLaterMutation = useChargePayLaterBooking();
    const markPaidMutation = useMarkPayLaterAsPaid();
    const markRefundedMutation = useMarkManualPaymentRefunded();
    const refundSeatMutation = useAdminRefundSeat();
    const summaryBg = useColorModeValue('gray.50', 'gray.700');

    // State for charge modal
    const [selectedPaymentIntent, setSelectedPaymentIntent] = useState<PaymentIntentDto | null>(null);
    const [customAmount, setCustomAmount] = useState<string>('');

    // State for paid-by side panel
    const {
        isOpen: isPaidByOpen,
        onOpen: onPaidByOpen,
        onClose: onPaidByClose,
    } = useDisclosure();
    const [paidByRow, setPaidByRow] = useState<UserWithPayment | null>(null);

    const allUsersWithPayments = useMemo(() => {
        const result: UserWithPayment[] = [];
        const participantMap = new Map<string, GameParticipantDto>();

        game.participants.forEach(participant => {
            participantMap.set(participant.participant.id, participant);
        });

        // Index covered participants per PI so we can divide the batch PI
        // amount equally across the covered share. A PI with userId=Alice and
        // covered = [Bob, Carla] charges Alice once, and each covered slot's
        // share is PI.amount / 2.
        //
        // Self-pay PIs (userId === a participant who has no paidBy) cover
        // exactly one slot — themselves — so their share == PI.amount.
        const coveredParticipantsByPi = new Map<string, GameParticipantDto[]>();
        game.participants.forEach((p) => {
            if (p.paidBy && p.paidBy.id !== p.participant.id) {
                // The PI that charged THIS seat (admin view stamps it); older
                // payloads fall back to the payer's first PI.
                const pi =
                    game.paymentIntents.find(
                        (intent) => intent.id === p.coveredPaymentIntentId,
                    ) ??
                    game.paymentIntents.find(
                        (intent) => intent.userId === p.paidBy!.id,
                    );
                if (pi) {
                    const list = coveredParticipantsByPi.get(pi.id) ?? [];
                    list.push(p);
                    coveredParticipantsByPi.set(pi.id, list);
                }
            }
        });

        game.paymentIntents.forEach(paymentIntent => {
            const coveredForPi = coveredParticipantsByPi.get(paymentIntent.id);

            if (coveredForPi && coveredForPi.length > 0) {
                // Batch PI — emit one row per covered player, plus
                // the payer themselves if they're also a participant.
                const ownerParticipant = participantMap.get(paymentIntent.userId);
                const totalBeneficiaries = coveredForPi.length + (ownerParticipant ? 1 : 0);
                const shareCents = Math.round(
                    paymentIntent.amount / totalBeneficiaries,
                );
                coveredForPi.forEach((participant) => {
                    result.push({
                        user: participant.participant,
                        paymentIntent,
                        isCurrentParticipant: true,
                        joinedAt: participant.createdAt,
                        position: participant.position,
                        paidBy: participant.paidBy,
                        coveredAmountCents:
                            participant.coveredAmountCents ?? shareCents,
                    });
                });
                if (ownerParticipant) {
                    result.push({
                        user: ownerParticipant.participant,
                        paymentIntent,
                        isCurrentParticipant: true,
                        joinedAt: ownerParticipant.createdAt,
                        position: ownerParticipant.position,
                    });
                }
                return;
            }

            // Non-batch PI (legacy self-pay or unknown user).
            const participant = participantMap.get(paymentIntent.userId);

            if (participant) {
                result.push({
                    user: participant.participant,
                    paymentIntent,
                    isCurrentParticipant: true,
                    joinedAt: participant.createdAt,
                    position: participant.position
                });
            } else {
                result.push({
                    user: {
                        id: paymentIntent.userId,
                        firstName: 'Unknown',
                        lastName: 'User',
                        email: '',
                        avatar: undefined,
                        level: 0
                    },
                    paymentIntent,
                    isCurrentParticipant: false
                });
            }
        });

        game.participants.forEach(participant => {
            const isCoveredByBatch = !!(
                participant.paidBy &&
                participant.paidBy.id !== participant.participant.id &&
                game.paymentIntents.some(
                    (pi) => pi.userId === participant.paidBy!.id,
                )
            );
            const hasOwnPi = game.paymentIntents.some(
                (pi) => pi.userId === participant.participant.id,
            );
            if (!isCoveredByBatch && !hasOwnPi) {
                result.push({
                    user: participant.participant,
                    paymentIntent: undefined,
                    isCurrentParticipant: true,
                    joinedAt: participant.createdAt,
                    position: participant.position
                });
            }
        });

        // Covered players who were KICKED (or left) with their share still
        // charged: they hold no seat, so nothing above emitted a row for them
        // and the operator had no refund button to reach. The API lists exactly
        // the shares that nobody has refunded.
        (game.formerCoveredSeats ?? []).forEach((seat) => {
            const paymentIntent = game.paymentIntents.find(
                (pi) => pi.id === seat.coveredPaymentIntentId,
            );
            if (!paymentIntent || participantMap.has(seat.participant.id)) {
                return;
            }
            result.push({
                user: seat.participant,
                paymentIntent,
                isCurrentParticipant: false,
                paidBy: seat.paidBy,
                coveredAmountCents: seat.coveredAmountCents,
            });
        });

        return result;
    }, [game.participants, game.paymentIntents, game.formerCoveredSeats]);

    const getPaymentStatusBadge = (paymentIntent?: PaymentIntentDto) => {
        if (!paymentIntent) {
            // No payment record at all — distinct from a payment that exists and
            // is genuinely in flight. Rendering both as "pending" told operators
            // a paying player still owed money (prod game 11541, 2026-07-28).
            return <Badge colorScheme="orange">{translate('game.payment_no_record')}</Badge>;
        }

        // Check if this is a pay later booking
        if (paymentIntent.isSetupIntentPayment && paymentIntent.status === PaymentIntentStatus.pay_later) {
            return <Badge colorScheme="brand">{translate('game.payment_pay_later')}</Badge>;
        }

        // Manual payment badges
        if (paymentIntent.paymentMethodType === 'manual_card') {
            return <Badge colorScheme="green">{translate('game.payment_manual_card')}</Badge>;
        }
        if (paymentIntent.paymentMethodType === 'manual_cash') {
            return <Badge colorScheme="green">{translate('game.payment_manual_cash')}</Badge>;
        }

        switch (paymentIntent.status) {
            case 'succeeded':
                return <Badge colorScheme="green">{translate('game.payment_completed')}</Badge>;
            case 'processing':
            case 'requires_action':
            case 'requires_confirmation':
            case 'requires_payment_method':
                return <Badge colorScheme="yellow">{translate('game.payment_pending')}</Badge>;
            case 'canceled':
                return <Badge colorScheme="red">{translate('game.payment_cancelled')}</Badge>;
            case 'refunded':
                return <Badge colorScheme="orange">{translate('game.payment_refunded')}</Badge>;
            case 'refunding':
                return <Badge colorScheme="yellow">{translate('game.payment_refunding')}</Badge>;
            default:
                return <Badge colorScheme="red">{translate('game.payment_failed')}</Badge>;
        }
    };

    const formatAmount = (paymentIntent?: PaymentIntentDto) => {
        if (!paymentIntent) return '-';
        const amount = paymentIntent.amount / 100; // Convert from cents
        return `${amount.toFixed(2)} ${paymentIntent.currency.toUpperCase()}`;
    };

    const handleChargePayLaterBooking = (paymentIntent: PaymentIntentDto) => {
        setSelectedPaymentIntent(paymentIntent);
        // Set default amount to original booking amount (in dollars/euros)
        const amountInDollars = paymentIntent.amount / 100;
        setCustomAmount(amountInDollars.toFixed(2));
        onOpen();
    };

    const handleConfirmCharge = () => {
        if (!selectedPaymentIntent) return;

        const amountInCents = Math.round(parsePrice(customAmount) * 100);

        chargePayLaterMutation.mutate({
            paymentIntentId: selectedPaymentIntent.id,
            options: {
                customAmount: amountInCents,
            }
        });

        onClose();
        setSelectedPaymentIntent(null);
        setCustomAmount('');
    };

    const handleOpenPaidBy = (row: UserWithPayment) => {
        if (
            !row.paidBy ||
            !row.paymentIntent ||
            !isCoveredSlot(row.user.id, row.paidBy)
        ) {
            return;
        }
        setPaidByRow(row);
        onPaidByOpen();
    };

    const paymentSummary = useMemo(() => {
        let totalExpectedCents = 0;
        let totalPaidCents = 0;
        let currency = '';

        // Money that has gone back does not count as held, and does not count
        // as owed either. A PARTIAL refund leaves its intent `succeeded` at
        // full amount and a FULL one flips the intent to `refunded` — netting
        // by intent is the only thing that tells those two apart, which is why
        // the API publishes `paymentIntentId` on each refund row. See
        // `payment-summary.utils.ts` for the whole rule.
        const refundedCentsByPi = liveRefundCentsByPaymentIntentId(
            game.refunds ?? [],
        );

        game.paymentIntents.forEach((pi) => {
            if (!currency) currency = pi.currency;
            const retained = retainedCents(pi, refundedCentsByPi.get(pi.id) ?? 0);
            totalExpectedCents += retained;
            if (pi.status === PaymentIntentStatus.succeeded) {
                totalPaidCents += retained;
            }
        });

        // Participants with no payment record at all still owe their share, and
        // summing only the payment rows made them vanish from the totals — the
        // footer read "Total en attente: 0.00" next to three unpaid-looking
        // players (prod game 11541).
        const participantsWithoutPayment = allUsersWithPayments.filter(
            (row) => row.isCurrentParticipant && !row.paymentIntent,
        ).length;

        // …but only on a game where each participant is billed individually.
        //
        // On a PRIVATE game one person pays for the whole court and the others
        // join free (`recomputeGameFullyPaid`: PRIVATE is fully paid the moment
        // the court booking PI succeeds), and when the booker did not name a
        // `playersPayingFor` those friends get no `paidBy` and no PI at all —
        // so billing each of them a seat invented an outstanding balance on a
        // game that was paid in full. A COMPED game is the same shape with no
        // PI anywhere: the venue gave the slot away, and the footer was
        // dunning the operator for it. Anyone who joins a PRIVATE game beyond
        // its paid slots is refused at the door (`PAYMENT_REQUIRED_FOR_PRIVATE_GAME`)
        // and pays through the joining rail, which leaves a PI — so a private
        // participant with no payment record never owes anything.
        const seatsStillOwed =
            game.type === GameType.PUBLIC && !game.isComped
                ? participantsWithoutPayment
                : 0;

        // What ONE player is charged, taken from the field the API computes
        // with the same calculator the joining rails bill from. The legacy
        // division is kept as a fallback for an API that predates the field:
        // it is only right when no membership discount applies (the discount is
        // one-seat scoped, so the whole-court amount over `capacity` overstates
        // a seat by up to `discount * (capacity - 1) / capacity`), but it beats
        // showing nothing pending, which is the bug it was added for.
        const perSeatPrice =
            game.defaultJoiningPricePerPlayer ??
            (game.defaultJoiningPrice && game.capacity > 0
                ? game.defaultJoiningPrice / game.capacity
                : undefined);
        if (seatsStillOwed > 0 && perSeatPrice != null) {
            totalExpectedCents += seatsStillOwed * Math.round(perSeatPrice * 100);
        }

        return {
            totalExpected: totalExpectedCents / 100,
            totalPaid: totalPaidCents / 100,
            currency: currency ? currency.toUpperCase() : 'EUR',
            participantsWithoutPayment,
        };
    }, [
        game.paymentIntents,
        game.refunds,
        game.defaultJoiningPrice,
        game.defaultJoiningPricePerPlayer,
        game.capacity,
        game.type,
        game.isComped,
        allUsersWithPayments,
    ]);

    if (allUsersWithPayments.length === 0) {
        return (
            <Box textAlign="center" py={8}>
                <Text fontSize="lg" color="gray.500">
                    {translate('game.no_participants')}
                </Text>
            </Box>
        );
    }

    return (
        <>
            <TableContainer>
                <Table variant="simple">
                    <Thead>
                        <Tr>
                            <Th>{translate('game.participants')}</Th>
                            <Th>{translate('game.participation_status')}</Th>
                            <Th>{translate('game.joined_at')}</Th>
                            <Th>{translate('game.payment_status')}</Th>
                            <Th isNumeric>{translate('game.payment_amount')}</Th>
                            <Th>{translate('game.paid_by')}</Th>
                            <Th>{translate('game.actions')}</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {allUsersWithPayments.map((userWithPayment, index) => (
                            <UserRow
                                key={`${userWithPayment.user.id}-${userWithPayment.paymentIntent?.id || index}`}
                                userWithPayment={userWithPayment}
                                navigateToUserDetails={navigateToUserDetails}
                                getPaymentStatusBadge={getPaymentStatusBadge}
                                formatAmount={formatAmount}
                                handleChargePayLaterBooking={handleChargePayLaterBooking}
                                chargePayLaterMutation={chargePayLaterMutation}
                                markPaidMutation={markPaidMutation}
                                markRefundedMutation={markRefundedMutation}
                                refundSeatMutation={refundSeatMutation}
                                gameId={game.id}
                                onOpenPaidBy={handleOpenPaidBy}
                            />
                        ))}
                    </Tbody>
                </Table>
            </TableContainer>

            {(game.paymentIntents.length > 0 ||
                paymentSummary.participantsWithoutPayment > 0) && (
                <HStack spacing={6} px={6} py={4} bg={summaryBg} borderRadius="md" mt={4} justify="space-between">
                    <HStack spacing={1}>
                        <Text fontWeight="bold">{translate('game.total_expected')}:</Text>
                        <Text>{paymentSummary.totalExpected.toFixed(2)} {paymentSummary.currency}</Text>
                    </HStack>
                    <HStack spacing={1}>
                        <Text fontWeight="bold" color="green.600">{translate('game.total_paid')}:</Text>
                        <Text color="green.600">{paymentSummary.totalPaid.toFixed(2)} {paymentSummary.currency}</Text>
                    </HStack>
                    <HStack spacing={1}>
                        <Text fontWeight="bold" color="orange.500">{translate('game.total_pending')}:</Text>
                        <Text color="orange.500">
                            {Math.max(0, paymentSummary.totalExpected - paymentSummary.totalPaid).toFixed(2)} {paymentSummary.currency}
                        </Text>
                    </HStack>
                </HStack>
            )}

            {/* Charge Payment Modal */}
            <FormModal
                isOpen={isOpen}
                onClose={onClose}
                title={translate('game.charge_pay_later_modal_title')}
                onSubmit={handleConfirmCharge}
                submitLabel={translate('game.charge_button')}
                isSubmitting={chargePayLaterMutation.isLoading}
                isSubmitDisabled={!customAmount || parseFloat(customAmount) <= 0}
            >
                {selectedPaymentIntent && (
                    <FormStack>
                        <Text>
                            {translate('game.original_booking_amount')}: <strong>{formatAmount(selectedPaymentIntent)}</strong>
                        </Text>
                        <FormField
                            label={`${translate('game.amount_to_charge')} (${selectedPaymentIntent.currency.toUpperCase()})`}
                            value={customAmount}
                            onChange={setCustomAmount}
                            placeholder={translate('game.enter_amount')}
                            helperText={translate('game.charge_custom_amount_hint')}
                            inputProps={{ type: 'number', step: '0.01', min: '0' }}
                        />
                    </FormStack>
                )}
            </FormModal>

            {paidByRow && paidByRow.paidBy && paidByRow.paymentIntent && (
                <PaidByDetailsDrawer
                    isOpen={isPaidByOpen}
                    onClose={() => {
                        onPaidByClose();
                        setPaidByRow(null);
                    }}
                    payer={paidByRow.paidBy}
                    coveredUser={paidByRow.user as UserDto}
                    paymentIntentId={paidByRow.paymentIntent.id}
                    amountCents={paidByRow.coveredAmountCents ?? paidByRow.paymentIntent.amount}
                    currency={paidByRow.paymentIntent.currency}
                />
            )}
        </>
    );
};

export default PaymentsTable;
